import Swiper from 'swiper'
import { Zoom } from 'swiper/modules'

import 'swiper/css'
import 'swiper/css/zoom'

/**
 * Plan zoom — fill the one shared dialog from whichever control was pressed,
 * then let the drawing be magnified once it is there.
 *
 * MicroModal opens the dialog itself: every control also carries
 * `data-micromodal-trigger`, and the MicroModal.init in app.js binds the click
 * that shows it. This only has to get the right drawing in place first, which is
 * why it listens on the document in the *capture* phase — capture always runs
 * ahead of a bubble-phase listener bound to the button, whichever order the two
 * modules happen to be initialised in.
 *
 * One dialog rather than one per drawing is what keeps a dozen full-size plans
 * out of the document: only the one being looked at is ever requested.
 *
 * The file comes off the drawing already on the page rather than from an
 * attribute on the control. `currentSrc` is whichever source the browser
 * actually picked out of the <picture> — so the dialog opens on a file that is
 * already in cache, and the URL stays written down in exactly one place.
 *
 * Swiper is here for its Zoom module and nothing else — no navigation, no
 * pagination, one slide. It is already in the bundle for the three carousels,
 * so the module is the only added weight, and the site keeps one modal system
 * rather than gaining a second lightbox alongside MicroModal's legal dialogs.
 */
export function initPlanZoom() {
    const modal = document.getElementById('plan-zoom-modal')
    if (!modal) return

    const image = modal.querySelector('[data-plan-zoom-image]')
    const title = modal.querySelector('[data-plan-zoom-title]')
    const frame = modal.querySelector('[data-plan-zoom-swiper]')
    const stepIn = modal.querySelector('[data-plan-zoom-in]')
    const stepOut = modal.querySelector('[data-plan-zoom-out]')
    if (!image || !title || !frame || !stepIn || !stepOut) return

    const swiper = new Swiper(frame, {
        modules: [Zoom],
        // The dialog is hidden until MicroModal shows it, so Swiper measures a
        // zero-height frame on init and every gesture would then work against
        // the wrong bounds. The observers re-measure when MicroModal flips the
        // dialog visible, which keeps the fix here rather than in app.js's
        // MicroModal.init — that one is shared with the footer's legal modals
        // and has no business knowing about this page.
        observer: true,
        observeParents: true,
        // 4x rather than the default 3x: these are line drawings whose room
        // labels are set small enough that 3x still leaves them squinting at on
        // a phone.
        zoom: { maxRatio: 4, toggle: true },
        // Dragging is left enabled because the zoom module pans through the
        // same touch events, but with one slide there is nowhere to drag to —
        // so the give at the edges is taken out rather than left to rubber-band
        // against a track that cannot move.
        //
        // The pan itself relies on patches/swiper@12.2.0.patch. Unpatched, Zoom
        // hands any drag that starts against an edge to the slider — and a
        // portrait plan at 2x in a landscape dialog is against both side edges,
        // so it could not be dragged at all. Re-check the patch on a Swiper bump.
        resistanceRatio: 0,
    })

    /**
     * Walk the scale one whole step.
     *
     * Swiper's own `zoom.in()` jumps straight to maxRatio, which is too blunt
     * for a drawing someone is reading — it is either fitted or four times over,
     * with nothing in between. Passing it a ratio is supported and gives the
     * stops back.
     *
     * `floor` going up and `ceil` coming down, so a step always moves off a
     * scale a pinch left part-way between two stops rather than snapping back
     * onto the one it just left.
     */
    const stepZoom = (direction) => {
        const { minRatio, maxRatio } = swiper.params.zoom
        const current = swiper.zoom.scale
        const next = direction > 0 ? Math.floor(current) + 1 : Math.ceil(current) - 1

        // `zoom.out()` rather than `zoom.in(1)`: it is the path that also clears
        // the zoomed state off the slide.
        if (next <= minRatio) swiper.zoom.out()
        else swiper.zoom.in(Math.min(next, maxRatio))
    }

    /**
     * Grey out whichever stepper has nowhere left to go.
     *
     * Pinch and double-tap move the same scale the buttons do, so the ends of
     * the range are read off the scale rather than tracked alongside it.
     *
     * The scale is taken as an argument because `zoomChange` is emitted from
     * inside Swiper's `scale` setter *before* it assigns — reading
     * `swiper.zoom.scale` in the handler returns the value being replaced, and
     * both buttons end up one press behind.
     *
     * Mind the handler signature below: Swiper puts its own instance in front
     * of every event's arguments, so the scale is the *second* one. Taking the
     * first silently compares an object against the ratios, which is false
     * every time — the buttons then never disable rather than disabling late,
     * which is the harder version to spot.
     */
    const syncSteppers = (scale = swiper.zoom.scale) => {
        const { minRatio, maxRatio } = swiper.params.zoom
        stepOut.disabled = scale <= minRatio
        stepIn.disabled = scale >= maxRatio
    }

    stepIn.addEventListener('click', () => stepZoom(1))
    stepOut.addEventListener('click', () => stepZoom(-1))
    swiper.on('zoomChange', (_swiper, scale) => syncSteppers(scale))
    syncSteppers()

    document.addEventListener(
        'click',
        (event) => {
            const trigger = event.target.closest?.('[data-plan-zoom]')
            if (!trigger) return

            // `src` covers the case where the drawing is still lazy-loading and
            // the browser has not settled on a source yet.
            const drawing = trigger.closest('[data-plan-figure]')?.querySelector('img')
            if (!drawing) return

            const label = trigger.dataset.planZoomLabel
            image.src = drawing.currentSrc || drawing.src
            image.alt = label
            // Names the dialog, which is labelled by this element.
            title.textContent = label

            // The dialog is shared, so a drawing swapped in while the last one
            // was left magnified would open part-way into a plan nobody chose.
            // A no-op when nothing is zoomed.
            swiper.zoom.out()
            // Reopening at 1x emits no zoomChange to sync against, so the
            // steppers are put back by hand.
            syncSteppers()
        },
        true
    )
}
